<template>
  <div style="width: 100%; height: 100%;">
    <div
      v-if="chartLoader"
      class="loader-container"
    >
      <v-progress-circular
        indeterminate
        color="green"
        width="2"
        size="38"
      />
    </div>
    <BarChart
      v-else
      ref="barChart"
      :chart-data="chartData"
      :options="options"
      :styles="styles"
      @chart:rendered="$emit('stop-loader')"
    />
    <Tooltip
      v-if="tooltipModel.opacity"
      :model="tooltipModel"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { BarChart } from '@/components/charts'
import { getRelativeDates } from '@/utils/time'
import { setCommas } from '@/utils/helpers'
import Tooltip from './Tooltip'

export default {
  name: 'BarLineChart',
  components: {
    BarChart,
    Tooltip
  },
  props: {
    dailyVisits: {
      type: Array,
      required: true
    },
    dailyAmount: {
      type: Array,
      required: true
    },
    appointed: {
      type: Array,
      required: true
    },
    deadlines: {
      type: Array,
      required: true
    },
    warningBar: {
      type: Array,
      required: true
    },
    loader: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      colors: {
        line: {
          border: '#13bb3d',
          background: 'rgba(19, 187, 61, 0.2)'
        },
        barAlpha: {
          today: 0.85,
          past: 0.3,
          future: 0.6
        }
      },
      styles: {
        height: '100%',
        width: '100%',
        position: 'relative'
      },
      tooltipModel: {}
    }
  },
  computed: {
    ...mapState('dashboard', ['chartLoader']),
    dates () {
      return getRelativeDates(7, -1, true)
    },
    options () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          rtl: true,
          labels: {
            filter: item => item.text === '件数' || item.text === '給油量'
          }
        },
        scales: {
          xAxes: [{
            stacked: true,
            gridLines: {
              display: false
            }
          }],
          yAxes: [{
            id: 'B',
            position: 'left',
            ticks: {
              callback: function (value) {
                return value + '件'
              },
              beginAtZero: true,
              stepSize: 1,
              min: 0,
              // if max value === 56 { max: 70} (min max = 10)
              max: Math.ceil(Math.max(...this.dailyVisits, 1) / 10) * 10,
              maxTicksLimit: 10
            }
          },
          {
            id: 'A',
            position: 'right',
            gridLines: {
              display: false
            },
            ticks: {
              callback: function (value) {
                // 4桁以上の場合下3桁削除、それでも4桁以上行く場合コンマを追加。
                const arr = setCommas(value).split(',')
                const isBiggerThanThousand = arr.length >= 2
                let last = ''
                if (isBiggerThanThousand) {
                  const p = arr.pop()
                  if (p !== '000') last = `.${p.charAt(0)}`
                }
                const str = arr.join(',')
                // 1000以上の場合"k"を付与する. 上で削除した下3桁が0以外の場合その1桁目を追加。 ("500"の場合".5")
                return isBiggerThanThousand ? str + last + 'kℓ' : str + 'ℓ'
              },
              beginAtZero: true,
              stepSize: 50,
              min: 0,
              // if max value === 4567 { max: 5000} (min max = 1000)
              max: Math.ceil(Math.max(...this.dailyAmount, 10) / 1000) * 1000,
              maxTicksLimit: 10
            }
          }]
        },
        tooltips: {
          enabled: false,
          mode: 'index',
          custom: m => {
            const position = this.$refs.barChart.$refs.canvas.getBoundingClientRect()
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.tooltipModel = { ...m, position }
          }
        }
      }
    },
    chartData () {
      return {
        labels: this.dates,
        datasets: [
          { // Line
            type: 'line',
            label: '給油量',
            order: 0,
            yAxisID: 'A',
            data: this.dailyAmount,
            lineTension: 0.1,
            pointBorderWidth: 3,
            pointHoverBorderWidth: 5,
            borderColor: this.colors.line.border,
            backgroundColor: this.colors.line.background
          },
          { // Bar
            label: '今日中残',
            order: 1,
            barPercentage: 0.6,
            backgroundColor: 'rgb(225, 110, 20)',
            data: this.warningBar
          },
          { // Bar
            label: '件数',
            order: 2,
            yAxisID: 'B',
            data: this.dailyVisits,
            backgroundColor: (ctx) => {
              let alpha = 0
              if (ctx.dataIndex < 1) {
                alpha = this.colors.barAlpha.past
              } else if (ctx.dataIndex === 1) {
                alpha = this.colors.barAlpha.today
              } else {
                alpha = this.colors.barAlpha.future
              }
              return `rgba(255, 160, 20, ${alpha})`
            },
            // borderColor: '#df992a',
            borderWidth: ctx => {
              return ctx.dataIndex === 1
                ? 8
                : 2
            },
            hoverBorderWidth: 10
          },
          { // hidden
            type: 'scatter',
            label: '猶予切れ',
            order: 3,
            borderWidth: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHoverBorderWidth: 0,
            data: this.deadlines
          },
          { // hidden
            type: 'scatter',
            label: '日付指定',
            order: 4,
            borderWidth: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHoverBorderWidth: 0,
            data: this.appointed
          }
        ]
      }
    }
  },
  methods: {
    getDataByIndex (arr, ...index) {
      return arr.map((el, i) => {
        return index.some(idx => i === idx)
          ? el
          : 0
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.loader-container{
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
}
</style>
